/**
 * Traducoes PT-BR para tipos brutos vindos do provedor BaaS (Asaas). Mantemos
 * em um lugar so pra reuso entre listagem do extrato, tela de detalhes e PDF.
 * Como a plataforma eh white-label, NUNCA exibir a string "Asaas" na UI —
 * sempre traduzir ou usar termos genericos ("provedor", "operacao").
 */

export const PROVIDER_TYPE_LABELS: Record<string, string> = {
    // Pagamentos recebidos
    PAYMENT_RECEIVED: 'Pagamento recebido',
    PAYMENT_DUNNING_RECEIVED: 'Pagamento (cobrança) recebido',
    PAYMENT_OVERDUE_RECEIVED: 'Pagamento (atrasado) recebido',
    PAYMENT_CREATED: 'Cobrança criada',
    ASAAS_MONEY_RECEIVED: 'Recebimento de saldo',
    PAYMENT_CONFIRMED: 'Pagamento confirmado',
    PAYMENT_ANTICIPATED: 'Pagamento antecipado',
    PAYMENT_AUTHORIZED: 'Pagamento autorizado',

    // Taxas
    PAYMENT_FEE: 'Tarifa de pagamento',
    FEE_COLLECTION: 'Cobrança de tarifa',
    ASAAS_FEE: 'Tarifa do provedor',
    INVOICE_FEE: 'Tarifa de nota fiscal',
    CREDIT_BUREAU_REPORT: 'Consulta de crédito',
    RECEIVABLE_ANTICIPATION_FEE: 'Tarifa de antecipação',

    // Estornos/reversões
    PAYMENT_REFUND: 'Estorno de pagamento',
    PAYMENT_REFUND_CANCELLED: 'Cancelamento de estorno',
    PAYMENT_REVERSED: 'Pagamento revertido',
    PAYMENT_FEE_REVERSED: 'Tarifa estornada',
    CHARGED_FEE_REFUND: 'Reembolso de tarifa',

    // Chargebacks
    PAYMENT_CHARGEBACK: 'Chargeback',
    PAYMENT_CHARGEBACK_REVERSAL: 'Reversão de chargeback',

    // Transferências
    TRANSFER: 'Transferência enviada',
    TRANSFER_REVERSAL: 'Transferência revertida',
    INTERNAL_TRANSFER_DEBIT: 'Transferência interna (saída)',
    INTERNAL_TRANSFER_CREDIT: 'Transferência interna (entrada)',
    INTERNAL_TRANSFER_REVERSAL: 'Transferência interna estornada',
    PIX_TRANSFER_DEBIT: 'PIX enviado',
    PIX_TRANSFER_CREDIT: 'PIX recebido',
    PIX_TRANSFER_REVERSAL: 'PIX estornado',

    // Recargas / boletos / outros
    MOBILE_PHONE_RECHARGE: 'Recarga de celular',
    REVERSED_MOBILE_PHONE_RECHARGE: 'Recarga estornada',
    BILL_PAYMENT: 'Pagamento de conta',
    BILL_PAYMENT_REVERSAL: 'Pagamento de conta estornado',
    ASAAS_CARD_BILL_PAYMENT: 'Pagamento de fatura',

    // Splits
    PAYMENT_SPLIT: 'Split enviado',
    PAYMENT_SPLIT_RECEIVED: 'Split recebido',

    // Outros
    DEBIT_AUTHORIZATION: 'Autorização de débito',
    RECEIVABLE_ANTICIPATION: 'Antecipação de recebíveis',
}

/**
 * Nosso enum interno (LedgerEntryType) também precisa de label PT-BR.
 */
export const INTERNAL_TYPE_LABELS: Record<string, string> = {
    CREDIT_PAYMENT: 'Pagamento recebido',
    FEE: 'Tarifa',
    TRANSFER_OUT: 'Transferência enviada',
    REFUND: 'Estorno',
    CHARGEBACK: 'Chargeback',
    SPLIT_IN: 'Split (entrada)',
    SPLIT_OUT: 'Split (saída)',
    ADJUSTMENT: 'Ajuste',
}

/**
 * Resolve o melhor label para uma entrada do extrato. Prefere o tipo bruto do
 * provedor (mais especifico), cai no nosso enum quando o bruto eh desconhecido.
 */
export function resolveOperationLabel(entry: {
    type?: string
    asaasType?: string | null
    raw?: any
}): string {
    const providerType = entry.asaasType ?? entry.raw?.asaasType ?? entry.raw?.type
    if (providerType && PROVIDER_TYPE_LABELS[providerType]) {
        return PROVIDER_TYPE_LABELS[providerType]
    }
    if (entry.type && INTERNAL_TYPE_LABELS[entry.type]) {
        return INTERNAL_TYPE_LABELS[entry.type]
    }
    return providerType || entry.type || 'Movimentação'
}

/**
 * Pega os campos do payload bruto que fazem sentido pra um operador NAO-DEV
 * ver na tela. Traduz chaves tecnicas pra labels amigaveis e formata valores.
 * Tudo que nao reconhece eh descartado (zero JSON cru no UI).
 */
export interface FriendlyField {
    label: string
    value: string
}

export function extractFriendlyFields(raw: any): FriendlyField[] {
    if (!raw || typeof raw !== 'object') return []

    const fields: FriendlyField[] = []
    const add = (label: string, value: any) => {
        if (value == null || value === '') return
        fields.push({ label, value: String(value) })
    }
    const addBrl = (label: string, value: any) => {
        const num = Number(value)
        if (!Number.isFinite(num) || num === 0) return
        fields.push({
            label,
            value: num.toLocaleString('pt-BR', {
                style: 'currency',
                currency: 'BRL',
            }),
        })
    }
    const addDate = (label: string, value: any) => {
        if (!value) return
        const d = new Date(value)
        if (Number.isNaN(d.getTime())) return
        fields.push({
            label,
            value: d.toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' }),
        })
    }

    // Pagador (entrada de PIX/cobranca)
    const payer = raw.payer ?? raw.customer ?? raw.pixTransaction?.payer
    if (payer && typeof payer === 'object') {
        add('Pagador', payer.name)
        add('CPF/CNPJ do pagador', payer.cpfCnpj ?? payer.document)
        add('Banco do pagador', payer.bankName ?? payer.bank?.name)
    } else if (typeof payer === 'string') {
        add('Pagador', payer)
    }

    // Destinatario (transferencia/pix saida)
    const dest = raw.bankAccount ?? raw.transfer?.bankAccount
    if (dest && typeof dest === 'object') {
        add('Favorecido', dest.ownerName ?? dest.holderName)
        add('CPF/CNPJ do favorecido', dest.cpfCnpj ?? dest.ownerBirthDate)
        add('Banco', dest.bank?.name ?? dest.bankName)
        add('Agência', dest.agency)
        add('Conta', dest.account)
    }

    // PIX
    add('Chave PIX', raw.pixAddressKey ?? raw.pixKey ?? raw.transfer?.pixAddressKey)
    add('Tipo de chave', raw.pixAddressKeyType ?? raw.pixKeyType)
    add('EndToEnd ID', raw.endToEndIdentifier ?? raw.endToEndId)

    // Boleto / conta
    add('Linha digitável', raw.identificationField)
    add('Beneficiário', raw.beneficiary?.name ?? raw.beneficiaryName)

    // Recarga
    add('Telefone', raw.phoneNumber ?? raw.mobilePhoneRecharge?.phoneNumber)
    add('Operadora', raw.operator ?? raw.mobilePhoneRecharge?.operator)

    // Datas relevantes
    addDate('Data da operação', raw.date ?? raw.transactionDate ?? raw.dateCreated)
    addDate('Data prevista', raw.scheduleDate ?? raw.estimatedCreditDate)
    addDate('Data de pagamento', raw.paymentDate ?? raw.transfer?.dateCreated)
    addDate('Data de vencimento', raw.dueDate)
    addDate('Data de cancelamento', raw.cancellationDate)

    // Valores
    addBrl('Valor bruto', raw.value ?? raw.amount)
    addBrl('Valor líquido', raw.netValue)
    addBrl('Tarifa', raw.fee ?? raw.feeValue)
    addBrl('Juros', raw.interestValue)
    addBrl('Multa', raw.fineValue)
    addBrl('Desconto', raw.discountValue)

    // Status / metodo
    add('Forma de pagamento', raw.billingType ?? raw.paymentMethod)
    add('Status no provedor', raw.status)

    // Referencias
    add('ID externo', raw.externalReference)
    add('Descrição', raw.description ?? raw.transfer?.description)
    add('Observações', raw.notes ?? raw.observations)

    // Failure
    add('Motivo da falha', raw.failReason ?? raw.transfer?.failReason)

    return fields
}
